/**
 * W1-11a (app-registry-keymap): translation helper for the registry, keymap and router.
 *
 * Strings go through the i18n runtime when it knows the key; otherwise the English text of
 * `registry.messages.ts` is used directly (simple `{name}` interpolation), so shortcut labels and recorder reasons
 * read correctly even before the catalogues are loaded and in isolated unit tests.
 */

import type { MessageRef } from '../../core/util/types';
import { i18n } from '../../i18n/runtime';
import type { MessageKey } from '../../i18n/types';
import catalog from './registry.messages';

export type Params = Readonly<Record<string, string | number>>;

/** Translates a key (or MessageRef) to a display string. */
export type Translate = (key: MessageKey | string, params?: Params) => string;

const PREFIX = `${catalog.ns}.`;

/** `{name}` interpolation (the English fallback only uses plain placeholders). */
export function interpolate(text: string, params?: Params): string {
  if (params === undefined) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = params[name];
    return v === undefined ? whole : String(v);
  });
}

/** English text of a 'registry.*' key, or undefined for keys of other namespaces. */
export function englishText(key: string): string | undefined {
  return key.startsWith(PREFIX) ? catalog.messages[key.slice(PREFIX.length)] : undefined;
}

/** Default translator: i18n runtime when it has the key, English fallback otherwise, the key itself last. */
export const translate: Translate = (key, params) => {
  if (i18n.has(key)) return i18n.t(key, params);
  const en = englishText(key);
  return en === undefined ? i18n.t(key, params) : interpolate(en, params);
};

/** Translate a MessageRef with the given translator. */
export function translateRef(t: Translate, ref: MessageRef): string {
  return t(ref.key, ref.params);
}

/** Build a MessageRef (params omitted when undefined, for exactOptionalPropertyTypes). */
export function ref(key: MessageKey, params?: Params): MessageRef {
  return params === undefined ? { key } : { key, params };
}
